import React, { useState, useEffect } from "react";
import { Button, Form, Container, Row, Col } from "react-bootstrap";
import Select from "react-dropdown-select";
import moment from "moment";
import axios from "axios";
import { config } from "../../App";

const Loader = () => (
    <div className="loader-container">
        <img src="/images/loader.gif" alt="loading" />
    </div>
);

const Form16 = () => {

    const token = sessionStorage.getItem("token");
    const userId = sessionStorage.getItem("userId");
    const doj = sessionStorage.getItem("doj");
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [yearError, setYearError] = useState("");
    const [monthError, setMonthError] = useState("");
    const [years, setYears] = useState([]);
    const [months, setMonths] = useState([]);

    const [pdfBlob, setPdfBlob] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [fileName, setFileName] = useState("");
    const [isSearched, setIsSearched] = useState(false);
    const [rawPdfBytes, setRawPdfBytes] = useState(null);

    const getCalendarData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${config.endpoint}/payroll/GetForm16FinYearAndType?doj=${moment(doj, "DD/MMM/YYYY").format("DD/MM/YYYY")}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;
            if (!data?.status) return;

            const formattedAssessmentYears = data.FinYears.map((y) => ({
                label: y.FinYear,
                value: y.FinYear,
            }));

            const formattedDocumentTypes = data.Types.map((t) => ({
                label: t.TypeName,
                value: t.TypeCode,
            }));

            setYears(formattedAssessmentYears);
            setMonths(formattedDocumentTypes);

            const defaultAssessmentYear = formattedAssessmentYears.find(
                (y) => y.label === data.DefaultFinYear
            );

            if (defaultAssessmentYear) {
                setSelectedYear(defaultAssessmentYear);
            }

            const defaultType = formattedDocumentTypes.find(
                (t) => t.label === data.DefaultType
            );

            if (defaultType) {
                setSelectedDocumentType(defaultType);
            }

        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getCalendarData();
    }, []);

    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    const validateForm = () => {
        let isValid = true;
        setYearError("");
        setMonthError("");

        if (!selectedYear) {
            setYearError("Please select a Assessment Year");
            isValid = false;
        }

        if (!selectedDocumentType) {
            setMonthError("Please select a Document Type");
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSearched(true);
        setPdfBlob(null);
        setRawPdfBytes(null);

        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await axios.get(`${config.endpoint}/payroll/GetForm16Letter?employeeId=${userId}&finYear=${selectedYear.value}&typeCode=${selectedDocumentType.value}`, {
                //responseType: "blob",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const data = response.data;

            if (!data?.status) {
                throw new Error(data?.ResponseMessage || "API failed");
            }

            const base64String = data.FileBase64;

            if (!base64String) {
                throw new Error("No Base64 file received");
            }

            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            const blob = new Blob([byteArray], {
                type: data.ContentType || "form16/pdf",
            });

            const url = URL.createObjectURL(blob);

            setFileName(data.FileName);
            setPdfBlob(blob);
            setPdfUrl(url);
            setRawPdfBytes(byteArray.buffer);

        } catch (err) {
            console.error("fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!rawPdfBytes) return;

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = fileName || "Form_16.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            <section className="px-4">
                {loading && <Loader />}
                {loading && <div className="backdrop" />}
                <Container fluid>
                    <div className="card border-0 p-4 pb-2">
                        <h1 className="fs-22 fw-semibold mb-4">Form 16</h1>
                        <Row>
                            <Col lg={4} className="mb-4 pb-4">
                                <Form.Group controlId="year">
                                    <Form.Label className="mb-2">
                                        Assessment Year <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={years}
                                        values={selectedYear ? [selectedYear] : []}
                                        onChange={(vals) => {
                                            setSelectedYear(vals[0] || null);
                                            setYearError("");
                                        }}
                                        placeholder="Select Assessment Year"
                                        className="form-control shadow-none"
                                        dropdownHeight="240px"
                                        searchable
                                    />
                                    {yearError && (
                                        <div className="text-danger mt-1 small font-sm position-absolute">{yearError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 pb-4">
                                <Form.Group controlId="month">
                                    <Form.Label className="mb-2">
                                        Document Type <sup className="primary-text font-md">*</sup>
                                    </Form.Label>
                                    <Select
                                        options={months}
                                        values={selectedDocumentType ? [selectedDocumentType] : []}
                                        onChange={(vals) => {
                                            setSelectedDocumentType(vals[0] || null);
                                            setMonthError("");
                                        }}
                                        placeholder="Select Document Type"
                                        className="form-control"
                                        dropdownHeight="240px"
                                    />
                                    {monthError && (
                                        <div className="text-danger mt-1 small font-sm position-absolute">{monthError}</div>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col lg={4} className="mb-4 d-flex align-items-end pb-0 pb-xl-4 pb-lg-4">
                                <div className="d-flex gap-3">
                                    <Button type="submit" onClick={handleSubmit} className="btn btn-primary font-lg">Submit</Button>
                                    {pdfUrl && (<Button onClick={handleDownload} type="button" className="btn btn-secondary font-lg">Download</Button>)}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="pdfViewSec mt-5 mb-5 card border-0 p-4">
                        <Row>
                            <Col lg={12}>
                                {pdfUrl && (
                                    <div
                                        style={{
                                            position: "relative",
                                            height: "800px",
                                            border: "1px solid #ced4da",
                                            borderRadius: "6px",
                                            overflow: "hidden",
                                            background: "#f8f9fa",
                                        }}
                                    >
                                        <iframe
                                            src={`${pdfUrl}#toolbar=0&navpanes=0`}
                                            title={`Form 16 ${selectedDocumentType?.label || ""} ${selectedYear?.value || ""}`}
                                            width="100%"
                                            height="100%"
                                            style={{
                                                border: "none",
                                                position: "relative",
                                                zIndex: 2,
                                            }}
                                        />
                                    </div>
                                )}

                                {isSearched && !pdfUrl && !loading && (
                                    <div className="card border-0 p-4 text-center text-muted font-md">
                                        <img
                                            src="/images/data-not-found.svg"
                                            className="img-fluid m-auto mt-5 mb-5 pt-5"
                                            alt="No data"
                                        />
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Container>

            </section>
        </>
    )
}

export default Form16;